import type { Campaign } from '~/modules/campaign/types';

import { useCampaignUpdateMutation } from '~/api/graphql/mutations/campaignUpdate.generated';
import { useTrackingAnalytic } from '~/modules/tracking/hooks/useTrackingAnalytic';
import { useCampaignStore } from '~/modules/campaign';
import { EVENT_ANALYTIC } from '~/modules/tracking';
import { useToast } from '~/hooks/useToast';
import { CAMPAIGN_MSG } from '../constants';

export interface IUpdateCampaignInfo<T extends Campaign> {
  onUpdateCampaignInfo: (id: string, payload: Partial<T>) => Promise<T | undefined>;
}
export const useUpdateCampaignInfo = <T extends Campaign>(): IUpdateCampaignInfo<T> => {
  const { shopifyToast } = useToast();
  const { trackEvent } = useTrackingAnalytic();

  const upsertCampaign = useCampaignStore((state) => state.upsertCampaign);
  const getCampaignById = useCampaignStore((state) => state.getCampaignById);
  const updateCampaignMutation = useCampaignUpdateMutation();

  const onUpdateCampaignInfo = async (id: string, payload: Partial<T>): Promise<T | undefined> => {
    const campaign = getCampaignById(id);
    if (!campaign) return;

    const { campaignUpdate } = await updateCampaignMutation.mutateAsync({
      id: campaign.id,
      input: payload,
    });

    if (!campaignUpdate) {
      shopifyToast(CAMPAIGN_MSG.UPDATE.FAILED, { isError: true });
      return;
    }

    shopifyToast(CAMPAIGN_MSG.UPDATE.SUCCESS);
    upsertCampaign(campaignUpdate);
    trackEvent(EVENT_ANALYTIC.GEMX_CAMPAIGN_UPDATE, {
      campaign: {
        ...campaignUpdate,
      },
    });

    return campaignUpdate as T;
  };

  return {
    onUpdateCampaignInfo,
  };
};
